import {CopilotAnimation} from '@github-ui/copilot-animation'
import {SandboxView} from '@github-ui/sandbox-view'
import {BrowserIcon} from '@primer/octicons-react'
import {useEffect, useState} from 'react'

import {usePreviewUrl} from '../../contexts/PreviewUrlContext'
import type {File, ItemsMap, PreviewableContentIdentifier, VersionedItemsMap} from './content-preview-types'
import {useContentPreview} from './ContentPreviewContext'
import {FailureState} from './FailureState'
import styles from './HtmlPreview.module.css'

/**
 * Extracts the referenced style file names from the HTML content.
 */
function getReferencedStyles(htmlContent: string) {
  // grab all link tags
  const styleTags = Array.from(htmlContent.matchAll(/<link([^>]+)>/g))
    .map(tag => tag[1])
    .filter(tag => tag !== undefined)

  const styleReferences = []
  for (const style of styleTags) {
    // filter out non-stylesheet links
    const rel = /rel\s*=\s*"([^"]+)"/.exec(style)?.[1]
    if (rel !== 'stylesheet') continue

    // grab the href
    const href = /href\s*=\s*"([^"]+)"/.exec(style)?.[1]
    if (!href) continue

    styleReferences.push(href)
  }

  return styleReferences
}

/**
 * Takes a file reference and maps it to the latest version of the file's content.
 */
function mapReferenceToFile(
  reference: string,
  items: ItemsMap,
  versionedItems: VersionedItemsMap,
  resolveReferenceId?: (reference: string) => PreviewableContentIdentifier | null,
): File | null {
  const version: PreviewableContentIdentifier | null = resolveReferenceId
    ? resolveReferenceId(reference)
    : `file:${reference}`
  if (!version || !versionedItems.has(version)) return null

  const itemVersions = versionedItems.get(version) ?? []
  const lastVersion = itemVersions[itemVersions.length - 1]
  if (!lastVersion) return null

  const itemToAdd = items.get(lastVersion)
  if (!itemToAdd || itemToAdd.type !== 'file') return null

  return itemToAdd
}

/**
 * Iterate through referenced scripts/styles and add the latest version and the contents of each into a single string.
 * Returns a string of all scripts and files, as well as a list of files referenced. Null values in the list of files
 * are references to files that do not (yet) exist.
 */
function extractReferencedResourcesContent(
  htmlContent: string,
  items: ItemsMap,
  versionedItems: VersionedItemsMap,
  resolveReferenceId?: (reference: string) => PreviewableContentIdentifier | null,
) {
  const referencedScriptNames = Array.from(htmlContent.matchAll(/<script.*?src="(.*?)"/g))
    .map(script => script[1])
    .filter(script => script !== undefined)
  const referencedScriptFiles = referencedScriptNames.map(script =>
    mapReferenceToFile(script, items, versionedItems, resolveReferenceId),
  )

  const referencedStyleNames = getReferencedStyles(htmlContent) // /<link rel="stylesheet" href="([^"]+)"/g.exec(htmlContent) ?? []
  const referencedStyleFiles = referencedStyleNames.map(style =>
    mapReferenceToFile(style, items, versionedItems, resolveReferenceId),
  )

  const inlineScripts = Array.from(htmlContent.matchAll(/<script(?![^>]*\bsrc=).*?>(.*?)<\/script>/gms))
    .map(script => script[1])
    .filter(script => script !== undefined)

  const scripts = [
    ...referencedScriptFiles.map(file => file?.value ?? ''),
    ...inlineScripts,
    `document.dispatchEvent(new Event("DOMContentLoaded"))`,
  ].join('\n')

  return {
    scripts,
    styles: referencedStyleFiles.map(file => file?.value ?? '').join('\n'),
    files: referencedScriptFiles.concat(referencedStyleFiles),
  }
}

function extractReferencedExternalResources(htmlContent: string) {
  const externalScripts = Array.from(htmlContent.matchAll(/<script.*?src="(https:\/\/.*?)"/g))
    .map(script => script[1])
    .filter(script => script !== undefined)

  const headScripts = []
  const bodyScripts = []

  for (const script of externalScripts) {
    // eslint-disable-next-line github/unescaped-html-literal
    const headContents = htmlContent.substring(htmlContent.indexOf('<head'), htmlContent.indexOf('</head>'))
    if (headContents.includes(script)) {
      headScripts.push(script)
    } else {
      bodyScripts.push(script)
    }
  }

  const externalStyles = Array.from(htmlContent.matchAll(/<link rel="stylesheet" href="(https:\/\/.*?)"/g))
    .map(style => style[1])
    .filter(style => style !== undefined)

  return {
    headScripts,
    bodyScripts,
    styles: externalStyles,
  }
}

export function HtmlPreview({file, previewRevision = 0}: {file: File; previewRevision?: number}) {
  const previewUrl = usePreviewUrl()
  const {items, versionedItems} = useContentPreview()
  const {
    scripts,
    styles: stylesText,
    files,
  } = extractReferencedResourcesContent(file.value, items, versionedItems, file.resolveReferenceId)
  const [forceShow, setForceShow] = useState(false)
  const isLoading = (file.isStreaming || files.some(f => !f || f.isStreaming)) && !forceShow

  // purposefully run this on every render to reset the timer
  useEffect(() => {
    if (isLoading) {
      // if we go 3 seconds with no progress, go ahead and show the content
      const id = setTimeout(() => {
        setForceShow(true)
      }, 3000)
      return () => clearTimeout(id)
    }
  })

  const {headScripts, bodyScripts, styles: externalStyles} = extractReferencedExternalResources(file.value)

  // Surfaces without a preview URL have no viewscreen host for the sandbox.
  if (!previewUrl) {
    return (
      <FailureState
        title="HTML preview isn't available here"
        description="You can still read the file's source in the Code view."
        icon={BrowserIcon}
      />
    )
  }

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingHead}>
          <CopilotAnimation animationType="thinking" loopAnimation />
        </div>
        <p className={styles.loadingText}>Preparing your preview&hellip;</p>
      </div>
    )
  } else {
    return (
      <SandboxView
        key={`${file.id}:${previewRevision}`}
        inlineScripts={scripts}
        inlineStyles={stylesText}
        headScripts={headScripts}
        bodyScripts={bodyScripts}
        styles={externalStyles}
        content={file.value}
        viewscreenUrl={previewUrl}
      />
    )
  }
}
